import Icon, { IconType } from 'components/Icon';
import { FrameEntry, SiteFrameEntry, VisualEditFrameEntry } from 'dragonmouse-shared/interfaces';
import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectFrame } from 'redux/db/reducers';
import { setInspectorLeftPanelWidth } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { frameSiteUrl } from 'utils';
import ResizablePanel from '../ResizablePanel';
import EditInput from './EditInput';
import { EditEntry, SiteEntry, VisualEditEntry } from './Entry';
import * as S from './styles';

interface InspectorLeftPanelProps {
	frameId: string;
	prototypeWidth?: number;
}

const InspectorLeftPanel = ({ frameId, prototypeWidth = undefined }: InspectorLeftPanelProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const scrollingAreaRef = useRef<HTMLDivElement>(null);

	const { frame, inspectorLeftPanelWidth } = useSelector((r: RootReducer) => {
		return {
			frame: selectFrame(r.db, frameId),
			inspectorLeftPanelWidth: r.mode.inspectorLeftPanelWidth,
		};
	}, shallowEqual);

	const siteUrl = frame ? frameSiteUrl(frame) : null;
	const isImageFrame = !siteUrl;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Auto-scroll to bottom when new entries are added
	useEffect(() => {
		if (scrollingAreaRef.current && frame?.entries) {
			// Use setTimeout to ensure the DOM has updated with the new entries
			setTimeout(() => {
				if (scrollingAreaRef.current) {
					scrollingAreaRef.current.style.scrollBehavior = 'smooth';
					scrollingAreaRef.current.scrollTop = scrollingAreaRef.current.scrollHeight;
				}
			}, 100);
		}
	}, [frame?.entries?.length]);

	const activeVisualEditEntryId = useMemo(() => {
		if (!frame?.metadata.visualEditActive || !frame?.entries || !frame?.siteId) return null;

		const visualEditEntry = frame.entries.find(
			(e): e is VisualEditFrameEntry => e.kind === 'visualEdit' && e.siteId === frame.siteId
		);

		return visualEditEntry?.id ?? null;
	}, [frame?.metadata.visualEditActive, frame?.entries, frame?.siteId]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------
	const getEntrySiteId = (entry: FrameEntry): string | null => {
		if (entry.kind === 'visualEdit' && 'siteId' in entry) {
			return entry.siteId;
		}
		if (entry.kind === 'site') {
			return (entry as SiteFrameEntry).siteId;
		}
		return null;
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	if (!frame) return null;

	const renderEntries = () => {
		// legacy frames do not have a site entry. let's spoof one.
		// note that adding a single edit should create a site entry. so we don't need to handle the case
		// where we want to show frame entries despite there not being a site entry.
		if (
			!frame.entries ||
			frame.entries.length === 0 ||
			frame.entries.find((e) => e.kind === 'site') === undefined
		) {
			const legacyEntry: SiteFrameEntry = {
				kind: 'site',
				id: 'legacy',
				createdAt: new Date(),
				deletedAt: null,
				siteId: frame.siteId || '',
				label: 'Version 1',
			};
			return (
				<S.Entries>
					<SiteEntry entry={legacyEntry} frame={frame} frameId={frameId} />
				</S.Entries>
			);
		}

		// Sort entries: visual edits appear immediately after their site entry
		const sortedEntries = [...frame.entries].sort((a, b) => {
			const aCreatedAt = new Date(a.createdAt).getTime();
			const bCreatedAt = new Date(b.createdAt).getTime();

			const aSiteId = getEntrySiteId(a);
			const bSiteId = getEntrySiteId(b);

			// If entries belong to the same site, visual edits come after site entries
			if (aSiteId && bSiteId && aSiteId === bSiteId) {
				if (a.kind === 'site' && b.kind === 'visualEdit') return -1;
				if (a.kind === 'visualEdit' && b.kind === 'site') return 1;
			}

			// Otherwise sort by createdAt
			return aCreatedAt - bCreatedAt;
		});
		return (
			<S.Entries>
				{sortedEntries.map((entry) => {
					if (entry.kind === 'site') {
						return <SiteEntry key={entry.id} entry={entry} frame={frame} frameId={frameId} />;
					} else if (entry.kind === 'edit') {
						return <EditEntry key={entry.id} entry={entry} />;
					} else if (entry.kind === 'visualEdit') {
						return (
							<VisualEditEntry
								key={entry.id}
								entry={entry}
								frame={frame}
								frameId={frameId}
								isActive={activeVisualEditEntryId === entry.id}
							/>
						);
					}
					return null; // Skip other frame entries (e.g. comments)
				})}
			</S.Entries>
		);
	};

	const renderEmptyState = () => {
		return (
			<S.EmptyState>
				<S.EmptyStateIcon>
					<Icon type={IconType.FRAME} width={48} height={48} color={theme.gray[400]} />
				</S.EmptyStateIcon>
				<S.EmptyStateText>No editing available</S.EmptyStateText>
				<S.EmptyStateSubtext>
					This panel is for editing prototype frames. Imported images don't support version history
					or editing.
				</S.EmptyStateSubtext>
			</S.EmptyState>
		);
	};

	return (
		<S.PanelWrapper>
			<ResizablePanel
				resizeOnSide="right"
				size={inspectorLeftPanelWidth}
				onResize={(w) => dispatch(setInspectorLeftPanelWidth(w))}
			>
				<S.ScrollingArea ref={scrollingAreaRef}>
					{isImageFrame ? renderEmptyState() : renderEntries()}
				</S.ScrollingArea>
				{!isImageFrame && <EditInput frame={frame} prototypeWidth={prototypeWidth} />}
			</ResizablePanel>
		</S.PanelWrapper>
	);
};

export default InspectorLeftPanel;
